/**
 * Cookie that carries {@link PublicTierAttribution} from Publish to signup.
 * Its `Max-Age` is {@link PUBLIC_TIER_ATTRIBUTION_TTL}, so the browser deletes
 * attribution from an old public session.
 */
export const PUBLIC_TIER_ATTRIBUTION_COOKIE = 'buffer_public_tier_attribution'

/** Cookie lifetime, in milliseconds, for the public tier cookies. */
export const PUBLIC_TIER_ATTRIBUTION_TTL = 30 * 60 * 1000

/**
 * Cookie that an app sets to a new random value when the visitor withdraws
 * analytics consent. Publish ends a public session that started under a
 * different value.
 */
export const PUBLIC_TIER_CONSENT_VERSION_COOKIE =
  'buffer_public_tier_consent_version'

const COOKIEYES_CONSENT_COOKIE = 'cookieyes-consent'

/** The public tier action that started signup. */
export enum PublicSignupTrigger {
  SCHEDULE = 'schedule',
  SAVE_DRAFT = 'saveDraft',
  SHARE_NOW_PROMPT = 'shareNowPrompt',
  CONNECT_CHANNEL = 'connectChannel',
  NAV_CTA = 'navCta',
  OTHER = 'other',
}

/**
 * Public tier context that Publish writes when a visitor starts signup. Core
 * adds these properties to `Account Created`.
 *
 * @see domains/tracking-plan/src/buffer-tracking-plan/events/accounts/account-created.yaml
 */
export type PublicTierAttribution = {
  isPublicTier: true
  publicSessionId: string
  entrySource: string
  /** ISO 8601 time of the first public tier page view in the session. */
  firstEnteredPublicTierAt: string
  publicActionCount: number
  signupTrigger: PublicSignupTrigger
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isPublicSignupTrigger(value: unknown): value is PublicSignupTrigger {
  return Object.values<unknown>(PublicSignupTrigger).includes(value)
}

/**
 * Checks untrusted attribution against the `Account Created` property types.
 * The browser writes this value, so these checks keep bad data out of
 * analytics. They do not prove that the value is authentic.
 *
 * @returns Only the known properties, or `undefined` when a type is wrong.
 */
export function parsePublicTierAttribution(
  value: unknown,
): PublicTierAttribution | undefined {
  if (!isRecord(value)) return undefined
  const {
    isPublicTier,
    publicSessionId,
    entrySource,
    firstEnteredPublicTierAt,
    publicActionCount,
    signupTrigger,
  } = value
  if (
    isPublicTier !== true ||
    typeof publicSessionId !== 'string' ||
    typeof entrySource !== 'string' ||
    typeof firstEnteredPublicTierAt !== 'string' ||
    typeof publicActionCount !== 'number' ||
    !isPublicSignupTrigger(signupTrigger)
  )
    return undefined
  return {
    isPublicTier,
    publicSessionId,
    entrySource,
    firstEnteredPublicTierAt,
    publicActionCount,
    signupTrigger,
  }
}

/** CookieYes stores consent as comma-separated `category:yes|no` entries. */
function hasAnalyticsConsent(consent: unknown): boolean {
  return (
    typeof consent === 'string' &&
    consent.split(',').some((entry) => entry.trim() === 'analytics:yes')
  )
}

function parseJson(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return undefined
  }
}

/**
 * Reads attribution from request or browser cookies.
 *
 * @param cookies - Decoded cookie values by name.
 * @returns The attribution, or `undefined` without CookieYes analytics consent
 * or a valid attribution cookie.
 * @see parsePublicTierAttribution
 */
export function readPublicTierAttribution(
  cookies: Record<string, unknown> | undefined,
): PublicTierAttribution | undefined {
  if (!hasAnalyticsConsent(cookies?.[COOKIEYES_CONSENT_COOKIE]))
    return undefined
  const raw = cookies?.[PUBLIC_TIER_ATTRIBUTION_COOKIE]
  if (typeof raw !== 'string') return undefined
  return parsePublicTierAttribution(parseJson(raw))
}
